<template>
  <b-modal
    :id="modalId"
    visible
    size="lg"
    centered
    hide-footer
    scrollable
    modal-class="engineer-popup-modal"
    @hide="$emit('close')"
  >
    <template #modal-header>
      <div class="popup-header">
        <div class="popup-header-left">
          <div class="popup-avatar">
            <feather-icon
              icon="UserIcon"
              size="28"
            />
          </div>
          <div class="popup-header-text">
            <h4 class="popup-title">
              {{ name }}
            </h4>
            <span class="popup-source-badge">
              <feather-icon
                icon="LayersIcon"
                size="14"
              />
              {{ source }}
            </span>
          </div>
        </div>
        <div
          class="popup-close-btn"
          @click="$emit('close')"
        >
          <feather-icon
            icon="XIcon"
            size="20"
          />
        </div>
      </div>
    </template>

    <div
      v-if="userLoading"
      class="popup-loading"
    >
      <b-spinner
        variant="primary"
        type="grow"
        label="Loading"
      />
      <span class="loading-text">Loading engineer analysis...</span>
    </div>

    <div
      v-else-if="!getSingleUser"
      class="popup-empty"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="32"
      />
      <p>No data available for this engineer.</p>
    </div>

    <div
      v-else
      class="popup-content"
    >
      <!-- Key Stats Row -->
      <div class="stats-row">
        <div
          v-if="getSingleUser.total_count != null"
          class="stat-card stat-card-primary"
        >
          <div class="stat-card-icon">
            <feather-icon
              icon="HashIcon"
              size="20"
            />
          </div>
          <div class="stat-card-content">
            <span class="stat-card-value">{{ getSingleUser.total_count }}</span>
            <span class="stat-card-label">Total Count</span>
          </div>
        </div>
        <div
          v-if="getSingleUser.certificate_issued != null"
          class="stat-card stat-card-success"
        >
          <div class="stat-card-icon">
            <feather-icon
              icon="AwardIcon"
              size="20"
            />
          </div>
          <div class="stat-card-content">
            <span class="stat-card-value">{{ getSingleUser.certificate_issued }}</span>
            <span class="stat-card-label">Certificate Issued</span>
          </div>
        </div>
        <div
          v-if="getSingleUser.completed != null"
          class="stat-card stat-card-info"
        >
          <div class="stat-card-icon">
            <feather-icon
              icon="CheckCircleIcon"
              size="20"
            />
          </div>
          <div class="stat-card-content">
            <span class="stat-card-value">{{ getSingleUser.completed }}</span>
            <span class="stat-card-label">Completed</span>
          </div>
        </div>
        <div
          v-if="getSingleUser.pending != null || getSingleUser.total_pending != null"
          class="stat-card stat-card-warning"
        >
          <div class="stat-card-icon">
            <feather-icon
              icon="ClockIcon"
              size="20"
            />
          </div>
          <div class="stat-card-content">
            <span class="stat-card-value">{{ getSingleUser.total_pending != null ? getSingleUser.total_pending : getSingleUser.pending }}</span>
            <span class="stat-card-label">Pending</span>
          </div>
        </div>
      </div>

      <!-- All Stats Grid -->
      <div class="stats-grid">
        <div
          v-for="(value, key) in arrangedStats"
          :key="key"
          class="stat-row"
        >
          <div class="stat-row-label">
            <feather-icon
              :icon="getIconForKey(key)"
              size="16"
              class="stat-row-icon"
            />
            <span>{{ formatLabel(key) }}</span>
          </div>
          <div class="stat-row-value">
            {{ value }}
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { desiredOrderSaber, desiredOrderJeem1 } from '@/components/analysis-table/desired-order'
import KEY_ICON_MAP from './iconMapKey'

export default {
  props: {
    name: {
      type: String,
      default: () => '',
    },
    source: {
      type: String,
      default: () => '',
    },
    userData: {
      type: Array,
      default: () => [],
    },
    userLoading: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      modalId: 'engineer-info-modal',
    }
  },
  computed: {
    getSingleUser() {
      const engineer = this.getEngineerInfo(this.userData, this.name)
      if (!engineer?.sources) return null

      const entry = Object.entries(engineer.sources).find(([key]) => key === this.source)
      return entry ? entry[1] : null
    },
    arrangedStats() {
      const data = this.getSingleUser
      if (!data) return {}

      const orderMap = {
        jeem1: desiredOrderJeem1,
        saber: desiredOrderSaber,
      }
      const desiredOrder = orderMap[this.source]
      if (!desiredOrder) return data

      const ordered = {}
      desiredOrder.forEach(key => {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          ordered[key] = data[key]
        }
      })
      Object.keys(data).forEach(key => {
        if (!Object.prototype.hasOwnProperty.call(ordered, key)) {
          ordered[key] = data[key]
        }
      })
      return ordered
    },
  },
  methods: {
    getEngineerInfo(userDataParams, name) {
      return userDataParams?.find(user => {
        const userNameWords = user.engineer_name?.split(' ').slice(0, 2).join(' ').toLowerCase()
        const inputNameWords = name?.split(' ').slice(0, 2).join(' ').toLowerCase()
        return userNameWords === inputNameWords
      })
    },
    getIconForKey(key) {
      return KEY_ICON_MAP[key] || KEY_ICON_MAP.default
    },
    formatLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
  },
}
</script>

<style lang="scss" scoped>
.popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 1rem;
}

.popup-header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.popup-avatar {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popup-header-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.popup-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #5e5873;
  margin: 0;
}

.popup-source-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.25rem 0.6rem;
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
}

.popup-close-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b9b9c3;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    color: #5e5873;
  }
}

.popup-empty {
  text-align: center;
  padding: 3rem;
  color: #b9b9c3;

  svg {
    margin-bottom: 1rem;
    color: #ea5455;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
  }
}

.popup-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;

  .loading-text {
    margin-top: 0.75rem;
    font-size: 0.95rem;
    color: #b9b9c3;
  }
}

.popup-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid #ebe9f1;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(34, 41, 47, 0.08);
  }
}

.stat-card-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  .stat-card-primary & {
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
  }

  .stat-card-success & {
    background: rgba(40, 199, 111, 0.12);
    color: #28c76f;
  }

  .stat-card-info & {
    background: rgba(0, 207, 232, 0.12);
    color: #00cfe8;
  }

  .stat-card-warning & {
    background: rgba(255, 159, 67, 0.12);
    color: #ff9f43;
  }
}

.stat-card-content {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.stat-card-value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #5e5873;
}

.stat-card-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #b9b9c3;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.stats-grid {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.stat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  background: #fafafa;
  border-radius: 10px;
  border: 1px solid #f5f5f5;
  transition: all 0.2s;

  &:hover {
    background: #f8f8f8;
    border-color: #ebe9f1;
  }
}

.stat-row-label {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #5e5873;
}

.stat-row-icon {
  color: #7367f0;
  flex-shrink: 0;
}

.stat-row-value {
  font-size: 1rem;
  font-weight: 700;
  color: #5e5873;
}
</style>

<style lang="scss">
.engineer-popup-modal .modal-header {
  background: linear-gradient(135deg, #f8f7ff 0%, #fff 100%);
  border-bottom: 1px solid #ebe9f1;
  padding: 1.25rem 1.5rem;
  position: sticky;
  top: 0;
  z-index: 2;
}

.engineer-popup-modal .modal-dialog {
  max-height: 90vh;
}

.engineer-popup-modal .modal-body {
  padding: 1.5rem;
  max-height: calc(90vh - 70px);
  overflow-y: auto;
}
</style>
